import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi, setApiAuthRefresh, type LoginResponse } from '../api/client';

type Role = 'employee' | 'admin' | 'healthcare';

interface User {
  userId: string;
  orgId: string;
  role: Role;
  email?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isReady: boolean;
  /**
   * セッションがユーザーの明示的な操作以外で終了したかのフラグ。
   * - true: refresh 失敗 / 別タブからのログアウト伝搬 / 強制セッション終了
   * - false: 初期状態 / 自発的なログアウト / ログイン直後
   * PrivateRoute がこれを参照して /login への遷移時に state.sessionExpired を伝搬する。
   */
  sessionExpired: boolean;
}

interface AuthContextValue extends AuthState {
  login: (tenantCode: string, email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  /** バックエンドを呼ばずにセッションのみ破棄（401 時などトークン無効で logout API を叩かない用） */
  clearSession: (opts?: { expired?: boolean }) => void;
  getToken: () => string | null;
}

/**
 * Issue #23 (2026-05): refresh token を sessionStorage に移し、マルチタブ運用時の上書きを回避。
 * タブを閉じると再ログインが必要になるトレードオフ。
 *
 * 以前は refresh token を localStorage に保存していたが、複数タブで別ユーザー/別テナントに
 * ログインした際にトークンが上書きされ、後勝ちで意図しないセッションが他タブにも波及する問題があった。
 * sessionStorage はタブごとに独立しているため、タブ単位でのセッション分離を実現する。
 *
 * 2026-05-31: マルチタブ UX 強化対応
 * - logoutSignal: localStorage の storage イベント経由で他タブの「同一ユーザーのログアウト」を伝搬
 * - access/user の legacy localStorage 掃除を refresh と対称化（旧版から上げたユーザーの取りこぼし対策）
 * - sessionExpired フラグを AuthState に追加し、PrivateRoute がバナー表示を判断できるように
 */
const STORAGE_KEYS = {
  access: 'ceeu_mental_access_token',
  refresh: 'ceeu_mental_refresh_token',
  user: 'ceeu_mental_user',
  /** 他タブへのログアウト伝搬用シグナル（localStorage 経由で storage event を発火させる） */
  logoutSignal: 'ceeu_mental_logout_signal',
} as const;

function parseJwtPayload(token: string): Record<string, unknown> {
  try {
    const b = token.split('.')[1];
    return b ? JSON.parse(atob(b)) : {};
  } catch {
    return {};
  }
}

/** access / user について localStorage 旧版を sessionStorage に一度きり吸い上げ、localStorage 側は削除する */
function migrateLegacy(key: 'access' | 'user'): string | null {
  if (typeof localStorage === 'undefined') return null;
  const legacy = localStorage.getItem(STORAGE_KEYS[key]);
  if (!legacy) return null;
  sessionStorage.setItem(STORAGE_KEYS[key], legacy);
  localStorage.removeItem(STORAGE_KEYS[key]);
  return legacy;
}

function readStored(): Partial<AuthState> {
  // Issue #23 + 2026-05-31: access / user / refresh いずれも sessionStorage を正とし、
  // 旧版で localStorage に保存されていた値は一度だけ sessionStorage に吸い上げて localStorage 側を消す。
  const access = sessionStorage.getItem(STORAGE_KEYS.access) || migrateLegacy('access');
  let refresh = sessionStorage.getItem(STORAGE_KEYS.refresh);
  if (!refresh && typeof localStorage !== 'undefined') {
    const legacy = localStorage.getItem(STORAGE_KEYS.refresh);
    if (legacy) {
      sessionStorage.setItem(STORAGE_KEYS.refresh, legacy);
      localStorage.removeItem(STORAGE_KEYS.refresh);
      refresh = legacy;
    }
  }
  const userJson = sessionStorage.getItem(STORAGE_KEYS.user) || migrateLegacy('user');
  let user: User | null = null;
  if (userJson) {
    try {
      user = JSON.parse(userJson) as User;
    } catch {
      /* ignore */
    }
  }
  if (access && !user) {
    const payload = parseJwtPayload(access);
    user = {
      userId: (payload.sub as string) || (payload.user_id as string) || '',
      orgId: (payload.org_id as string) || '',
      role: (payload.role as Role) || 'employee',
    };
  }
  return { accessToken: access || null, refreshToken: refresh || null, user };
}

function broadcastLogout(userId: string) {
  if (typeof localStorage === 'undefined') return;
  try {
    const nonce =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : String(Math.random()).slice(2);
    localStorage.setItem(STORAGE_KEYS.logoutSignal, `${userId}:${nonce}`);
    localStorage.removeItem(STORAGE_KEYS.logoutSignal);
  } catch {
    /* ignore */
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isReady: false,
    sessionExpired: false,
  });

  useEffect(() => {
    const stored = readStored();
    setState((s) => ({
      ...s,
      ...stored,
      isReady: true,
    }));
  }, []);

  const persist = useCallback((res: LoginResponse) => {
    const access = res.access_token;
    const payload = parseJwtPayload(access);
    const user: User = {
      userId: (payload.sub as string) || (payload.user_id as string) || '',
      orgId: (payload.org_id as string) || '',
      role: (payload.role as Role) || 'employee',
    };
    sessionStorage.setItem(STORAGE_KEYS.access, access);
    // Issue #23: refresh token は sessionStorage に保存（タブ単位で分離）。
    if (res.refresh_token) {
      sessionStorage.setItem(STORAGE_KEYS.refresh, res.refresh_token);
    }
    const refreshStored = sessionStorage.getItem(STORAGE_KEYS.refresh);
    sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
    setState({
      user,
      accessToken: access,
      refreshToken: refreshStored,
      isReady: true,
      sessionExpired: false,
    });
  }, []);

  const login = useCallback(
    async (tenantCode: string, email: string, password: string): Promise<User> => {
      const res = await authApi.login(tenantCode, email, password);
      const payload = parseJwtPayload(res.access_token);
      const user: User = {
        userId: (payload.sub as string) || (payload.user_id as string) || '',
        orgId: (payload.org_id as string) || '',
        role: (payload.role as Role) || 'employee',
      };
      persist(res);
      return user;
    },
    [persist]
  );

  const clearSession = useCallback((opts?: { expired?: boolean }) => {
    sessionStorage.removeItem(STORAGE_KEYS.access);
    sessionStorage.removeItem(STORAGE_KEYS.refresh);
    sessionStorage.removeItem(STORAGE_KEYS.user);
    // 2026-05-31: legacy localStorage 側も access / refresh / user すべて掃除する（refresh のみ消す旧実装の取りこぼし対策）。
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.access);
      localStorage.removeItem(STORAGE_KEYS.refresh);
      localStorage.removeItem(STORAGE_KEYS.user);
    }
    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isReady: true,
      sessionExpired: !!opts?.expired,
    });
  }, []);

  /**
   * 保護 API が 401 のとき client.request から呼ばれる。
   * - refresh が無い: null を返す（セッションは各画面の 401 処理や PrivateRoute に任せる）
   * - POST /v1/auth/refresh が失敗: トークン削除（再ログインが必要）+ sessionExpired=true
   */
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // Issue #23: refresh token は sessionStorage を正とする。
    let rt = sessionStorage.getItem(STORAGE_KEYS.refresh);
    if (!rt && typeof localStorage !== 'undefined') {
      // 旧版で localStorage に保存されていた refresh の救済（一度きり sessionStorage に吸い上げる）。
      const legacy = localStorage.getItem(STORAGE_KEYS.refresh);
      if (legacy) {
        sessionStorage.setItem(STORAGE_KEYS.refresh, legacy);
        localStorage.removeItem(STORAGE_KEYS.refresh);
        rt = legacy;
      }
    }
    if (!rt) {
      return null;
    }
    try {
      const res = await authApi.refresh(rt);
      persist(res);
      return res.access_token;
    } catch {
      clearSession({ expired: true });
      return null;
    }
  }, [persist, clearSession]);

  useEffect(() => {
    setApiAuthRefresh(refreshAccessToken);
    return () => setApiAuthRefresh(null);
  }, [refreshAccessToken]);

  /**
   * 2026-05-31: 他タブからのログアウト伝搬を受信する。
   * - storage イベントは「他タブが localStorage を変更したとき」発火する（同タブでは発火しない）。
   * - 別タブの logout は broadcastLogout() で localStorage.logoutSignal に set→remove を実施するため、
   *   このタブには newValue 付き storage event が届く。
   * - 同一ユーザー ID のシグナルのみ反応する（Scenario C: 別ロール別タブ運用を壊さないため）。
   */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEYS.logoutSignal || !e.newValue) return;
      const [signaledUserId] = e.newValue.split(':');
      // 状態 closure ではなく sessionStorage の現在値を確認（古いレンダで判定しない）
      const userJson = sessionStorage.getItem(STORAGE_KEYS.user);
      if (!userJson) return;
      try {
        const u = JSON.parse(userJson) as User;
        if (u.userId === signaledUserId) {
          clearSession({ expired: true });
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [clearSession]);

  const logout = useCallback(async () => {
    const access = state.accessToken;
    const userId = state.user?.userId;
    if (access) {
      try {
        await authApi.logout(access);
      } catch {
        /* ignore */
      }
    }
    // 他タブ（同一ユーザー）にもログアウトを伝搬してから自タブのセッションを破棄する。
    if (userId) {
      broadcastLogout(userId);
    }
    clearSession();
  }, [state.accessToken, state.user?.userId, clearSession]);

  const getToken = useCallback(() => state.accessToken, [state.accessToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      clearSession,
      getToken,
    }),
    [state, login, logout, clearSession, getToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
